import { useCallback, useEffect, useRef, useState } from 'react';

import { isVisibleDialog } from './shouldIgnoreShortcut';
import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

interface UseDoBoxNavigationProps {
  totalItems: number;
  onOpenItem: (index: number) => void | Promise<void>;
  onCloseItem: (index: number) => void;
  isItemOpen: (index: number) => boolean;
  onOpenModal?: (index: number) => void;
  enabled?: boolean;
}

export function useDoBoxNavigation({
  totalItems,
  onOpenItem,
  onCloseItem,
  isItemOpen,
  onOpenModal,
  enabled = true,
}: UseDoBoxNavigationProps) {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts({
    disableFocusSteal: true,
  });

  const selectedIndexRef = useRef(selectedIndex);
  const totalItemsRef = useRef(totalItems);

  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    totalItemsRef.current = totalItems;
  }, [totalItems]);

  useEffect(() => {
    if (totalItems === 0) {
      setSelectedIndex(-1);
    } else if (selectedIndex >= totalItems) {
      setSelectedIndex(totalItems - 1);
    }
  }, [totalItems, selectedIndex]);

  const handleNavigateDown = useCallback(() => {
    const total = totalItemsRef.current;
    const current = selectedIndexRef.current;
    if (total === 0) {
      return;
    }
    if (current === -1) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(Math.min(current + 1, total - 1));
    }
  }, []);

  const handleNavigateUp = useCallback(() => {
    const total = totalItemsRef.current;
    const current = selectedIndexRef.current;
    if (total === 0) {
      return;
    }
    if (current === -1) {
      setSelectedIndex(total - 1);
    } else {
      setSelectedIndex(Math.max(current - 1, 0));
    }
  }, []);

  const handleHome = useCallback(() => {
    if (totalItemsRef.current === 0) {
      return;
    }
    setSelectedIndex(0);
  }, []);

  const handleEnd = useCallback(() => {
    const total = totalItemsRef.current;
    if (total === 0) {
      return;
    }
    setSelectedIndex(total - 1);
  }, []);

  const handleOpenItem = useCallback(async () => {
    const index = selectedIndexRef.current;
    const total = totalItemsRef.current;
    if (index >= 0 && index < total) {
      if (!isItemOpen(index)) {
        await onOpenItem(index);
      }
    }
  }, [isItemOpen, onOpenItem]);

  const handleCloseItem = useCallback(() => {
    const index = selectedIndexRef.current;
    const total = totalItemsRef.current;
    if (index >= 0 && index < total) {
      if (isItemOpen(index)) {
        onCloseItem(index);
      }
    }
  }, [isItemOpen, onCloseItem]);

  const handleEnter = useCallback(() => {
    const dialogs = document.querySelectorAll('[role="dialog"]');
    const hasVisiblePopover = Array.from(dialogs).some(isVisibleDialog);
    if (hasVisiblePopover) {
      return;
    }
    const index = selectedIndexRef.current;
    const total = totalItemsRef.current;
    if (index >= 0 && index < total && onOpenModal) {
      onOpenModal(index);
    }
  }, [onOpenModal]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    registerShortcut({
      key: ShortcutKey.ARROW_DOWN,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateDown,
      description: 'Navigate to next DoBox item',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_UP,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateUp,
      description: 'Navigate to previous DoBox item',
    });

    registerShortcut({
      key: ShortcutKey.NEXT,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateDown,
      description: 'Navigate to next DoBox item',
    });

    registerShortcut({
      key: ShortcutKey.PREVIOUS,
      contexts: [ShortcutContext.DOBOX],
      handler: handleNavigateUp,
      description: 'Navigate to previous DoBox item',
    });

    registerShortcut({
      key: ShortcutKey.HOME,
      contexts: [ShortcutContext.DOBOX],
      handler: handleHome,
      description: 'Navigate to first DoBox item',
    });

    registerShortcut({
      key: ShortcutKey.END,
      contexts: [ShortcutContext.DOBOX],
      handler: handleEnd,
      description: 'Navigate to last DoBox item',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_RIGHT,
      contexts: [ShortcutContext.DOBOX],
      handler: handleOpenItem,
      description: 'Open DoBox item dropdown',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_LEFT,
      contexts: [ShortcutContext.DOBOX],
      handler: handleCloseItem,
      description: 'Close DoBox item dropdown',
    });

    registerShortcut({
      key: ShortcutKey.ENTER,
      contexts: [ShortcutContext.DOBOX],
      handler: handleEnter,
      description: 'Open DoBox item modal',
    });

    const ctx = [ShortcutContext.DOBOX];
    return () => {
      unregisterShortcut(ShortcutKey.ARROW_DOWN, ctx);
      unregisterShortcut(ShortcutKey.ARROW_UP, ctx);
      unregisterShortcut(ShortcutKey.NEXT, ctx);
      unregisterShortcut(ShortcutKey.PREVIOUS, ctx);
      unregisterShortcut(ShortcutKey.HOME, ctx);
      unregisterShortcut(ShortcutKey.END, ctx);
      unregisterShortcut(ShortcutKey.ARROW_RIGHT, ctx);
      unregisterShortcut(ShortcutKey.ARROW_LEFT, ctx);
      unregisterShortcut(ShortcutKey.ENTER, ctx);
    };
  }, [
    enabled,
    registerShortcut,
    unregisterShortcut,
    handleNavigateDown,
    handleNavigateUp,
    handleHome,
    handleEnd,
    handleOpenItem,
    handleCloseItem,
    handleEnter,
  ]);

  return {
    selectedIndex,
    setSelectedIndex,
  };
}
